import { useSearchParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { getActivity, getAssignments } from "../api";
import { useShell } from "../Layout";
import { attentionHref, routes } from "../routes";
import { useAsync } from "../useAsync";
import {
  Badge, Card, CardBody, CardHead, DASH, Empty, Freshness, NearMiss, Pane, ScopeBar,
  When,
} from "../ui";

/**
 * Everything that happened in this cohort, newest first.
 *
 * 🔴 `audit_log` HAD NO READER. Six routes wrote to it — a Run pressed, a rubric bound,
 * coursework created and published, a mark released, a mark entered by hand — and none of it
 * was visible anywhere in the product. On a system whose whole design rests on being able to
 * say what happened to a student's mark, "who did this, and when?" was answerable only from a
 * database shell.
 *
 * ⚠️ THE FILTERS LIVE IN THE QUERY STRING, so a view is a link. "Every release on this
 * assignment" is something a teacher can send to somebody else.
 */
export function LogsPage() {
  const { courseId, access } = useShell();
  const [params, setParams] = useSearchParams();
  const kind = params.get("kind") ?? "";
  const cw = params.get("cw") ?? "";
  // 🔴 THE COHORT FILTER IS IN THE QUERY STRING LIKE EVERY OTHER ONE, so "every release
  // across all my cohorts" is a link somebody can send. Absent means narrowed to the cohort
  // you arrived from, which is the only default that keeps the page about what you clicked.
  const mine = params.get("scope") !== "all";
  const [before, setBefore] = [params.get("before") ?? "",
                               (v: string) => set("before", v)];

  const q = useAsync(
    // The wide read always; the server bounds it to what this teacher may view, and the
    // near-miss line has to be able to say what the filter is hiding.
    () => getActivity(courseId, { limit: 50, kind: kind || undefined, scope: "all",
                                  courseWorkId: cw || undefined, before: before || undefined }),
    [courseId, kind, cw, before]);
  const assignments = useAsync(() => getAssignments(courseId), [courseId]);

  function set(key: string, value: string) {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    // Changing a filter starts the list again; keeping `before` would page from a cursor
    // that belongs to a different query.
    if (key !== "before") next.delete("before");
    setParams(next, { replace: true });
  }

  return (
    <div className="stack">
      <div className="page-head">
        <p className="eyebrow">Operations</p>
        <h2>Logs</h2>
        <span className="fresh"><Freshness q={q} /></span>
      </div>

      <Card>
        <CardHead
          title="Activity"
          desc={<>What a person did, what the unattended sweep did, and what each run produced.
                Every row links to the thing it names.</>} />
        <ScopeBar name={access.name} on={mine} count={q.data?.rows.length ?? 0}
                  onChange={(on) => set("scope", on ? "" : "all")} />
        <div className="log-filters">
          <button className={kind ? "chip" : "chip on"} aria-pressed={!kind}
                  onClick={() => set("kind", "")}>all</button>
          {(q.data?.kinds ?? []).map((k) => (
            <button key={k.id} className={kind === k.id ? "chip on" : "chip"}
                    aria-pressed={kind === k.id}
                    onClick={() => set("kind", kind === k.id ? "" : k.id)}>{k.label}</button>))}
          <span className="spacer" />
          <select className="select" value={cw} aria-label="Assignment"
                  onChange={(e) => set("cw", e.target.value)}>
            <option value="">All assignments</option>
            {(assignments.data?.assignments ?? []).map((a) => (
              <option key={a.course_work_id} value={a.course_work_id}>
                {a.title || a.course_work_id}</option>))}
          </select>
        </div>
        <CardBody tight>
          <Pane q={q} empty={<Empty title="Nothing has happened in this cohort yet"
                                    hint="A row appears when somebody presses Run, binds a
                                          rubric, releases a mark, or a grading run finishes." />}>
            {(d) => {
              // ⚠️ A GLOBAL SETTINGS CHANGE HAS NO COHORT and is never a near-miss: it
              // applies to this one as much as any other, so an empty `course_id` stays.
              const away = d.rows.filter(
                (r) => r.course_id !== "" && r.course_id !== courseId);
              return (<>
              <ul className="log-list">
                {d.rows.map((r, i) => (
                  <li className={mine && r.course_id !== "" && r.course_id !== courseId
                                 ? "log-row faint" : "log-row"} key={`${r.at}-${i}`}>
                    <span className="when"><When iso={r.at} /></span>
                    <span className="actor" title={r.actor}>
                      {/* ⚠️ A RUN NOBODY ASKED FOR HAS NO ACTOR AND MUST NOT BORROW ONE. */}
                      {r.actor === "sweep"
                        ? <Badge tone="held">sweep</Badge>
                        : (r.actor || DASH)}
                    </span>
                    <span>{r.text}</span>
                    <span className="go">
                      {r.submission_id
                        ? <Link to={routes.submission(courseId, r.submission_id)}>report →</Link>
                        : <Link to={attentionHref(courseId, r)}>{r.page} →</Link>}
                    </span>
                  </li>))}
              </ul>
              {mine && <NearMiss n={away.length}
                                 onInclude={() => set("scope", "all")} />}
              </>); }}
          </Pane>
        </CardBody>
        <CardBody>
          <div className="row gap">
            <button className="btn sm" disabled={!q.data || q.data.rows.length < 50}
                    onClick={() => setBefore(q.data?.rows.at(-1)?.at ?? "")}>Older</button>
            {before && <button className="btn sm ghost"
                               onClick={() => setBefore("")}>Back to newest</button>}
            <span className="small muted">{q.data?.note}</span>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
